import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./viewFranchiseManger.css";
import {
  getFranchiseManagerDetail,
  changeFranchiseManagerDocumentStatus,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import ViewFranchiseManagerHistory from "./viewFranchiseManagerHistory";
import ViewFranchiseManagerRejectModal from "./viewFranchiseManagerRejectModal";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

// Human-readable label for a per-document status
const docStatusLabel = (status) => {
  if (status === "approved") return "Verified";
  if (status === "rejected") return "Rejected";
  return "Pending";
};

const ViewFranchiseManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { franchise_id, city_head_id, locality_head_id } = params;
  const id = params.id || locality_head_id || city_head_id;

  const backPath = locality_head_id
    ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads`
    : city_head_id
      ? `/franchise/${franchise_id}/city-heads`
      : "/franchise";

  const roleLabel = locality_head_id
    ? "Locality Head"
    : city_head_id
      ? "City Head"
      : "Franchise Manager";

  const editPath = locality_head_id
    ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/${locality_head_id}/edit`
    : city_head_id
      ? `/franchise/${franchise_id}/city-heads/${city_head_id}/edit`
      : `/franchise/edit/${id}`;

  // const accessControlPath = locality_head_id
  //   ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/${locality_head_id}/access-control`
  //   : city_head_id
  //     ? `/franchise/${franchise_id}/city-heads/${city_head_id}/access-control`
  //     : `/franchise/access-control/${id}`;

  const [showHistory, setShowHistory] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // null = whole profile, else document_id (e.g. "aadhar")

  const {
    franchiseManagerDetail,
    detailLoading = false,
    documentStatusUpdating = false,
  } = useSelector((state) => state.franchiseManager || {});

  useEffect(() => {
    if (id) {
      dispatch(getFranchiseManagerDetail(id)).catch(() => {
        toast.error("Failed to load franchise manager details");
      });
    }
  }, [id, dispatch]);

  const data = franchiseManagerDetail;

  const getInitials = (name) => {
    if (!name) return "FM";
    const parts = name.trim().split(" ");
    return parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  const handleApproveDocuments = (document_id = null) => {
    dispatch(
      changeFranchiseManagerDocumentStatus({
        id,
        approval: "approved",
        ...(document_id && { document_id }),
      })
    )
      .then(() => dispatch(getFranchiseManagerDetail(id, false)))
      .catch(() => {
        toast.error("Failed to approve documents");
      });
  };

  const handleRejectDocuments = (reason) => {
    dispatch(
      changeFranchiseManagerDocumentStatus({
        id,
        approval: "rejected",
        reason,
        ...(rejectTarget && { document_id: rejectTarget }),
      })
    )
      .then(() => dispatch(getFranchiseManagerDetail(id, false)))
      .catch(() => {
        toast.error("Failed to reject documents");
      });
    setShowRejectModal(false);
    setRejectTarget(null);
  };

  if (detailLoading && !data) {
    return (
      <MainLayout>
        <div className="vfm-wrapper">
          <div className="vfm-loading">
            <div className="vfm-spinner-lg" />
            <span>Loading franchise manager...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!detailLoading && !data) {
    return (
      <MainLayout>
        <div className="vfm-wrapper">
          <div className="vfm-empty-state">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3>{roleLabel} not found</h3>
            <Link to={backPath} className="vfm-btn vfm-btn--primary">
              Back to list
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const documentList = data.document_list || [];

  // Derive the overall KYC status from the individual documents instead of
  // trusting `data.document_verification_status` blindly — the backend field
  // can drift out of sync with the per-document `isVerified`/`status` values,
  // which is what produced the "Approved" header next to "Unverified" docs.
  const derivedDocStatus =
    documentList.length === 0
      ? null
      : documentList.every((d) => d.isVerified)
        ? "approved"
        : documentList.some((d) => d.status === "rejected")
          ? "rejected"
          : "pending";

  const hasRejectedDoc = documentList.some((d) => d.status === "rejected");
  const allRejected =
    documentList.length > 0 && documentList.every((d) => d.status === "rejected");

  const showApproveAll = derivedDocStatus !== "approved" && !hasRejectedDoc;
  const showRejectAll = derivedDocStatus !== "approved" && !allRejected;

  return (
    <MainLayout>
      <div className="vfm-wrapper">
        {/* Header */}
        <div className="vfm-header">
          <div className="vfm-header-left">
            <button className="vfm-back-btn" onClick={() => navigate(backPath)} type="button" aria-label="Go back">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div>
              <h1 className="vfm-title">{roleLabel} Details</h1>
              <p className="vfm-subtitle">View complete profile information</p>
            </div>
          </div>

          <div className="vfm-header-actions">
            <Link to={`/franchise/access-control/${id}`} className="vfm-history-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="10" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Access
            </Link>

            {data.history?.length > 0 && (
              <button
                className="vfm-history-btn"
                onClick={() => setShowHistory(true)}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                History
              </button>
            )}

            <Link to={editPath} className="vfm-edit-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </Link>
          </div>
        </div>

        {/* Profile summary card */}
        <div className="vfm-profile-card">
          <div className="vfm-avatar">
            {data.profile_image ? (
              <img src={data.profile_image} alt={data.name} className="vfm-avatar-img" />
            ) : (
              <span className="vfm-avatar-initials">{getInitials(data.name)}</span>
            )}
          </div>

          <div className="vfm-profile-info">
            <div className="vfm-profile-name-row">
              <h2 className="vfm-profile-name">{data.name || "—"}</h2>
              <span className={`vfm-status-badge vfm-status-badge--${data.status === "active" ? "active" : "inactive"}`}>
                <span className="vfm-status-dot" />
                {data.status === "active" ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="vfm-profile-role">{data.role || "—"}</p>

            <div className="vfm-profile-meta">
              <span className="vfm-profile-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16v16H4z" opacity="0" />
                  <path d="M22 6l-10 7L2 6" />
                  <path d="M2 6h20v12H2z" />
                </svg>
                {data.email || "—"}
                {data.email && (
                  <span
                    className={`vfm-verify-badge ${data.is_email_verified ? "vfm-verify-badge--yes" : "vfm-verify-badge--no"
                      }`}
                  >
                    {data.is_email_verified ? "Verified" : "Unverified"}
                  </span>
                )}
              </span>
              <span className="vfm-profile-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                {data.country_code} {data.contact_number || "—"}
                {data.contact_number && (
                  <span
                    className={`vfm-verify-badge ${data.is_contact_number_verified ? "vfm-verify-badge--yes" : "vfm-verify-badge--no"
                      }`}
                  >
                    {data.is_contact_number_verified ? "Verified" : "Unverified"}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Details sections */}
        <div className="vfm-section-card">
          <div className="vfm-section-header">
            <span className="vfm-section-badge">01</span>
            <h3 className="vfm-section-title">Work Information</h3>
          </div>

          <div className="vfm-detail-grid">
            <div className="vfm-detail-item">
              <span className="vfm-detail-label">Admin ID</span>
              <span className="vfm-detail-value">{data.admin_id || "—"}</span>
            </div>
            <div className="vfm-detail-item">
              <span className="vfm-detail-label">Department</span>
              <span className="vfm-detail-value">{data.department || "—"}</span>
            </div>
            <div className="vfm-detail-item">
              <span className="vfm-detail-label">Designation</span>
              <span className="vfm-detail-value">{data.designation || "—"}</span>
            </div>
            <div className="vfm-detail-item">
              <span className="vfm-detail-label">Timezone</span>
              <span className="vfm-detail-value">{data.timezone || "—"}</span>
            </div>
            <div className="vfm-detail-item">
              <span className="vfm-detail-label">Joined On</span>
              <span className="vfm-detail-value">{formatDate(data.created_at)}</span>
            </div>
            <div className="vfm-detail-item vfm-detail-item--full">
              <span className="vfm-detail-label">Address</span>
              <span className="vfm-detail-value">{data.address || "—"}</span>
            </div>
          </div>
        </div>

        <div className="vfm-section-card">
          <div className="vfm-section-header">
            <span className="vfm-section-badge">02</span>
            <h3 className="vfm-section-title">Location & Access</h3>
          </div>

          <div className="vfm-tag-group">
            <span className="vfm-tag-group-label">City</span>
            <div className="vfm-tag-list">
              {data.city?.length > 0 ? (
                data.city.map((c) => (
                  <span className="vfm-tag vfm-tag--city" key={c._id}>
                    {c.city_name}
                  </span>
                ))
              ) : (
                <span className="vfm-detail-value">—</span>
              )}
            </div>
          </div>

          <div className="vfm-tag-group">
            <span className="vfm-tag-group-label">Locality</span>
            <div className="vfm-tag-list">
              {data.locality?.length > 0 ? (
                data.locality.map((l) => (
                  <span className="vfm-tag vfm-tag--locality" key={l._id}>
                    {l.name}
                  </span>
                ))
              ) : (
                <span className="vfm-detail-value">—</span>
              )}
            </div>
          </div>

          <div className="vfm-tag-group">
            <span className="vfm-tag-group-label">Project</span>
            <div className="vfm-tag-list">
              {data.project?.length > 0 ? (
                data.project.map((p) => (
                  <span className="vfm-tag vfm-tag--project" key={p._id}>
                    {p.project_name}
                  </span>
                ))
              ) : (
                <span className="vfm-detail-value">—</span>
              )}
            </div>
          </div>
        </div>

        {/* KYC Documents section — driven by document_list */}
        {documentList.length > 0 &&
          <div className="vfm-section-card">
            <div className="vfm-section-header">
              <span className="vfm-section-badge">03</span>
              <h3 className="vfm-section-title">KYC Documents</h3>
              {derivedDocStatus && (
                <span
                  className={`vfm-doc-status vfm-doc-status--${derivedDocStatus}`}
                >
                  {derivedDocStatus}
                </span>
              )}
            </div>

            {derivedDocStatus === "rejected" && data.document_rejection_reason && (
              <div className="vfm-doc-rejection">
                <span className="vfm-detail-label">Rejection Reason</span>
                <p>{data.document_rejection_reason}</p>
              </div>
            )}

            <div className="vfm-doc-list">
              {documentList.map((doc) => {
                const status = doc.status || (doc.isVerified ? "approved" : "pending");
                const hasImages = doc.frontSide || doc.backSide;
                return (
                  <div className="vfm-doc-card" key={doc.id}>
                    <div className="vfm-doc-card-main">
                      <div className="vfm-doc-card-heading">
                        <span className="vfm-doc-card-name">{doc.name}</span>
                        <span className={`vfm-verify-badge vfm-verify-badge--${status}`}>
                          {docStatusLabel(status)}
                        </span>
                      </div>
                      <span className="vfm-doc-card-value">{doc.value || "—"}</span>
                      {doc.reason && (
                        <p className="vfm-doc-card-reason">{doc.reason}</p>
                      )}
                    </div>

                    <div className="vfm-doc-card-side">
                      {hasImages ? (
                        <div className="vfm-doc-card-images">
                          {doc.frontSide && (
                            <a href={doc.frontSide} target="_blank" rel="noreferrer">
                              <img src={doc.frontSide} alt={`${doc.name} Front`} />
                            </a>
                          )}
                          {doc.requiresBoth && doc.backSide && (
                            <a href={doc.backSide} target="_blank" rel="noreferrer">
                              <img src={doc.backSide} alt={`${doc.name} Back`} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="vfm-doc-card-no-image">No file uploaded</span>
                      )}

                      {status !== "approved" && status !== "rejected" && (
                        <div className="vfm-doc-card-actions">
                          <button
                            className="vfm-doc-btn vfm-doc-btn--approve"
                            onClick={() => handleApproveDocuments(doc.id)}
                            disabled={documentStatusUpdating || doc.isVerified || status === "requested"}
                            type="button"
                          >
                            Approve
                          </button>
                          <button
                            className="vfm-doc-btn vfm-doc-btn--reject"
                            onClick={() => {
                              setRejectTarget(doc.id);
                              setShowRejectModal(true);
                            }}
                            disabled={documentStatusUpdating || status === "requested"}
                            type="button"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {(showApproveAll || showRejectAll) && (
              <div className="vfm-doc-actions">
                {showApproveAll && (
                  <button
                    className="vfm-doc-btn vfm-doc-btn--approve"
                    onClick={() => handleApproveDocuments()}
                    disabled={documentStatusUpdating}
                    type="button"
                  >
                    Approve All
                  </button>
                )}
                {showRejectAll && (
                  <button
                    className="vfm-doc-btn vfm-doc-btn--reject"
                    onClick={() => {
                      setRejectTarget(null);
                      setShowRejectModal(true);
                    }}
                    disabled={documentStatusUpdating}
                    type="button"
                  >
                    Reject All
                  </button>
                )}
              </div>
            )}
          </div>
        }

        <ViewFranchiseManagerHistory
          open={showHistory}
          onClose={() => setShowHistory(false)}
          history={data.history}
        />

        <ViewFranchiseManagerRejectModal
          open={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRejectTarget(null);
          }}
          onSubmit={handleRejectDocuments}
        />
      </div>
    </MainLayout>
  );
};

export default ViewFranchiseManager;